/**
 * Accounts settings tab - brokerage account management
 */
import { useState } from 'react'
import { useAccounts, useCreateAccount, useUpdateAccount, useDeleteAccount } from '../../hooks'
import { Button, Input, Modal, LoadingCard } from '../common'
import type { Account, AccountCreate, AccountUpdate, AccountType } from '../../types'
import { ACCOUNT_TYPE_LABELS } from '../../types'

export function AccountsTab() {
  const { data: accounts, isLoading } = useAccounts()
  const createAccount = useCreateAccount()
  const updateAccount = useUpdateAccount()
  const deleteAccount = useDeleteAccount()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [formData, setFormData] = useState<AccountCreate>({
    account_id: '',
    name: '',
    type: 'brokerage',
  })
  const [error, setError] = useState('')
  const [showFirstAccountSuccess, setShowFirstAccountSuccess] = useState(false)

  const openCreateModal = () => {
    setEditingAccount(null)
    setFormData({ account_id: '', name: '', type: 'brokerage' })
    setError('')
    setIsModalOpen(true)
  }

  const openEditModal = (account: Account) => {
    setEditingAccount(account)
    setFormData({
      account_id: account.account_id,
      name: account.name,
      type: account.type,
      institution: account.institution,
      notes: account.notes,
    })
    setError('')
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const isFirstAccount = !editingAccount && (!accounts || accounts.length === 0)

    try {
      if (editingAccount) {
        const update: AccountUpdate = {
          name: formData.name,
          institution: formData.institution,
          notes: formData.notes,
        }
        await updateAccount.mutateAsync({
          accountId: editingAccount.account_id,
          data: update,
        })
      } else {
        await createAccount.mutateAsync(formData)
        if (isFirstAccount) {
          setShowFirstAccountSuccess(true)
        }
      }
      setIsModalOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    }
  }

  const handleDelete = async (accountId: string) => {
    if (!confirm('Are you sure you want to delete this account?')) return

    try {
      await deleteAccount.mutateAsync(accountId)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete account')
    }
  }

  if (isLoading) {
    return <LoadingCard />
  }

  return (
    <div className="space-y-6">
      {/* First account success message */}
      {showFirstAccountSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-green-800">Account created!</h3>
              <p className="mt-1 text-sm text-green-700">
                Now you can add holdings to track your portfolio.
              </p>
              <div className="mt-3 flex gap-2">
                <a
                  href="/holdings"
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                >
                  Add Holdings
                  <svg className="ml-1.5 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
                <button
                  onClick={() => setShowFirstAccountSuccess(false)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-700 hover:text-green-800"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Brokerage Accounts</h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage your investment accounts and tax buckets
            </p>
          </div>
          <Button onClick={openCreateModal}>Add Account</Button>
        </div>

        <div className="divide-y divide-gray-200">
          {accounts?.length === 0 ? (
            <p className="px-6 py-8 text-center text-gray-500">
              No accounts configured. Add an account to get started.
            </p>
          ) : (
            accounts?.map((account) => (
              <div key={account.account_id} className="px-6 py-4 flex justify-between items-center">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{account.name}</h3>
                  <p className="text-sm text-gray-500">
                    {ACCOUNT_TYPE_LABELS[account.type]} |{' '}
                    {account.institution || 'No institution'} |{' '}
                    {account.holdings_count} holdings
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openEditModal(account)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(account.account_id)}
                    disabled={account.holdings_count > 0}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Account Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingAccount ? 'Edit Account' : 'Add Account'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingAccount && (
            <Input
              label="Account ID"
              value={formData.account_id}
              onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
              placeholder="e.g., schwab-brokerage"
              helperText="Lowercase letters, numbers, hyphens only"
              required
            />
          )}

          {editingAccount && (
            <div className="text-sm text-gray-500 mb-2">
              Account ID: <strong>{editingAccount.account_id}</strong>
            </div>
          )}

          <Input
            label="Display Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Schwab Brokerage"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Type
            </label>
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as AccountType })}
              disabled={!!editingAccount}
            >
              <option value="brokerage">Brokerage</option>
              <option value="physical">Physical Assets</option>
              <option value="fixed_income">Fixed Income</option>
            </select>
            {editingAccount && (
              <p className="mt-1 text-xs text-gray-500">Account type cannot be changed</p>
            )}
          </div>

          <Input
            label="Institution (optional)"
            value={formData.institution || ''}
            onChange={(e) => setFormData({ ...formData, institution: e.target.value || undefined })}
            placeholder="e.g., Charles Schwab"
          />

          <Input
            label="Notes (optional)"
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value || undefined })}
            placeholder="e.g., Primary trading account"
          />

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createAccount.isPending || updateAccount.isPending}
            >
              {editingAccount ? 'Save Changes' : 'Create Account'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
